import { metadataMatch, sha256 } from "yam-operator/util"
import { cacheGet, cacheSet } from "yam-operator/util/cache"
import { ConfigMap, K8SResourceType, KVString, OperateFunction, Secret } from "yam-types"
import { Settings } from "../settings.d"
import { ApplicationSecret, Configuration } from "../types.d"
import { patchVolumeAndEnv } from "./util"

const initConfigAndSecrets: OperateFunction<ApplicationSecret | Configuration> = async (plan, _, settings: Settings) => {
  if (!settings.initConfigMaps && !settings.initSecrets) {
    return
  }
  const cacheKey = 'core-config-init:' + plan.data.envName + '/' + plan.data.namespace
  const preHash = cacheGet(cacheKey)
  const hash = sha256(JSON.stringify(settings))
  const skipApply = preHash && preHash === hash
  const cfgSecList: (ConfigMap | Secret)[] = []
  const needMountCfg: unknown[] = []

  settings.initConfigMaps?.forEach(cfg => {
    let annotations = settings.configMapAnnotations
    if (cfg.metadata && cfg.metadata['annotations']) {
      annotations = {
        ...annotations,
        ...cfg.metadata['annotations'] as KVString
      }
    }
    const cfgEntry: ConfigMap = {
      name: cfg.name,
      data: cfg.data,
      namespace: cfg.namespace || plan.data.namespace,
      metadata: {
        ...cfg.metadata,
        annotations
      }
    }
    cfgSecList.push(cfgEntry)
    if (metadataMatch(plan.data.currentModelFull.metadata, cfg.mountWhenMetadataMatch)) {
      needMountCfg.push(cfg)
    }
  })
  settings.initSecrets?.forEach(sec => {
    let annotations = settings.configMapAnnotations
    if (sec.metadata && sec.metadata['annotations']) {
      annotations = {
        ...annotations,
        ...sec.metadata['annotations'] as KVString
      }
    }
    const secEntry: Secret = {
      name: sec.name,
      data: sec.data,
      namespace: sec.namespace || plan.data.namespace,
      secret: true,
      type: sec.type,
      metadata: {
        ...sec.metadata,
        annotations
      }
    }
    cfgSecList.push(secEntry)
    if (metadataMatch(plan.data.currentModelFull.metadata, sec.mountWhenMetadataMatch)) {
      needMountCfg.push(sec)
    }
  })
  if (!skipApply && cfgSecList.length > 0) {
    plan.log.info(`start to apply configMap/secret: ${cfgSecList.length} items.`)
    plan.action(async (ctx) => {
      // check if ConfigOrSecret namespace not exists
      const nss = cfgSecList.map(cfgSec => cfgSec.namespace).filter(ns => !!ns) as string[]
      for (const ns of nss) {
        if (!await ctx.k8sClient.namespaceExists(ns)) {
          // create namespace
          await ctx.k8sClient.apply({
            apiVersion: "v1",
            kind: K8SResourceType.Namespace,
            metadata: {
              name: ns
            }
          })
          ctx.log.info(`namespace ${ns} was not found and has been automatically created.`)
        }
      }

      for (const cfgSec of cfgSecList) {
        if (!await ctx.k8sClient.getConf(cfgSec)) {
          // If it does not exist saveConf
          await ctx.k8sClient.saveConf(cfgSec)
        }
      }
      if (!plan.data.dryRun) {
        cacheSet(cacheKey, hash)
      }
    }, `init-configMap-secret`)
  }

  if (needMountCfg.length > 0) {
    plan.log.info(`add config/secret mounting action: ${needMountCfg.map(x => (x as KVString).name).join(',')}`)
    plan.action(async (ctx) => {
      for (const cfgSec of needMountCfg) {
        const conf = cfgSec as Configuration
        patchVolumeAndEnv(ctx, conf, plan, conf.name, conf.secret ? 'secret' : 'configMap')
      }
    }, `mount-init-config-secret`)
  }
}

export default initConfigAndSecrets
